import cn from 'classnames';
import { MouseEventHandler } from 'react';
import { DropzoneState } from 'react-dropzone';
import { FormattedMessage, useIntl } from 'react-intl';

import { useMedia } from '@/shared/hooks';

import { Icon } from '../Icon';

import styles from './OrderReportDropzone.module.scss';

// https://github.com/facebook/react/issues/3468#issuecomment-1031366038
declare module 'react' {
  interface InputHTMLAttributes<T> extends HTMLAttributes<T> {
    // extends React's HTMLAttributes
    directory?: string;
    webkitdirectory?: string;
  }
}

type UploadStudyDropzoneProps = {
  dropzoneState: DropzoneState;
  canUploadFolder?: boolean;
};

export const OrderReportDropzone = (props: UploadStudyDropzoneProps) => {
  const {
    dropzoneState: { getRootProps, getInputProps, inputRef },
    canUploadFolder = true,
  } = props;

  const { formatMessage } = useIntl();
  const { isMobile } = useMedia();

  const inputProps = getInputProps();

  const uploadButtonLabel =
    inputProps.multiple === true
      ? formatMessage({
          id: 'orderReportDropzone.uploadFileButton.multiple',
          defaultMessage: 'Upload files',
        })
      : formatMessage({
          id: 'orderReportDropzone.uploadFileButton.single',
          defaultMessage: 'Upload file',
        });

  const handleUploadFolder: MouseEventHandler<HTMLDivElement> = (event) => {
    event.stopPropagation();
    inputRef.current?.click();
  };

  if (isMobile) {
    return (
      <div className={styles.mobileContainer} {...getRootProps()}>
        <input {...inputProps} />
        <p className={cn(styles.mobileTitle, 'p2')}>{uploadButtonLabel}</p>
      </div>
    );
  }

  return (
    <div
      data-component="order-report-dropzone"
      className={styles.container}
      {...getRootProps()}
    >
      <div className={styles.buttonsWrapper}>
        <div className={cn(styles.button)}>
          <input {...inputProps} />

          <Icon name="file" size={24} />

          <p className={cn(styles.title, 'p2')}>{uploadButtonLabel}</p>
        </div>

        {canUploadFolder && (
          <div className={cn(styles.button)} onClick={handleUploadFolder}>
            <input
              id="uploadFolderInput"
              {...inputProps}
              type="file"
              webkitdirectory=""
            />

            <Icon name="folder" size={28} />

            <p className={cn(styles.title, 'p2')}>
              <FormattedMessage
                id="orderReportDropzone.titleFolder"
                defaultMessage="Upload folder"
              />
            </p>
          </div>
        )}
      </div>

      <p className={cn(styles.text, 'p3')}>
        <FormattedMessage
          id="orderReportDropzone.text"
          defaultMessage="Or drag and drop file or folder here"
        />
      </p>
    </div>
  );
};
